import { useTranslation } from 'react-i18next';
import { Select } from '@/components/synapse/Select';
import { CLINIC_PICKER_PATH, globalHref, tenantHref } from '@/routing/tenant';
import type { ActiveClinic } from '@/types/api';

interface ClinicSwitcherProps {
  active: ActiveClinic | null;
  memberships: ActiveClinic[];
}

/** Sentinel option value; no clinic id can collide because ids are uuids. */
const ALL_CLINICS = 'all-clinics';

/**
 * The clinic this session is looking at, and — for anyone who works at more than
 * one — a way to move between them.
 *
 * Switching is a HARD navigation, not `navigate()`. Each clinic is its own URL
 * namespace and the router's `basename` is fixed at mount, so the router cannot
 * cross it. The full reload is also what guarantees the query cache does not
 * carry one clinic's patients into another clinic's shell.
 *
 * A member of exactly one clinic gets a plain label: no control, because there
 * is no choice. This sits where a static "Workspace" caption used to.
 */
export function ClinicSwitcher({ active, memberships }: ClinicSwitcherProps) {
  const { t } = useTranslation();
  const label = active?.name ?? t('nav.workspace');

  if (memberships.length < 2) {
    return (
      <div className="truncate px-2.5 pb-2 text-[11px] uppercase tracking-[0.08em] text-muted-2">
        {label}
      </div>
    );
  }

  function onPick(value: string): void {
    if (value === ALL_CLINICS) {
      window.location.assign(globalHref(CLINIC_PICKER_PATH));
      return;
    }
    const target = memberships.find((m) => m.id === value);
    if (!target || target.id === active?.id) return;
    window.location.assign(tenantHref(target.slug, '/'));
  }

  return (
    <div className="pb-2">
      <Select
        selectSize="sm"
        aria-label={t('nav.switchClinic')}
        value={active?.id ?? ''}
        onChange={(e) => onPick(e.target.value)}
        className="border-transparent bg-transparent text-[12px] font-medium hover:bg-paper-2"
      >
        {active === null && (
          <option value="" disabled hidden>
            {label}
          </option>
        )}
        {memberships.map((clinic) => (
          <option key={clinic.id} value={clinic.id}>
            {clinic.name}
          </option>
        ))}
        <option value={ALL_CLINICS}>{t('nav.allClinics')}</option>
      </Select>
    </div>
  );
}
